import type { CampaignBiddingUpdate, SpBiddingStrategy } from "@adpilot/shared";
import { IconCheck, IconPencil, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { Radio, RadioGroup } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/** The reference's order; rule-based is shown but disabled (Beta). */
const OPTIONS: readonly SpBiddingStrategy[] = [
  "LEGACY_FOR_SALES",
  "AUTO_FOR_SALES",
  "MANUAL",
  "RULE_BASED",
];

export interface BiddingStrategyPopoverProps {
  strategy: SpBiddingStrategy | null;
  pending: boolean;
  onSubmit: (strategy: NonNullable<CampaignBiddingUpdate["strategy"]>) => Promise<unknown>;
}

/** 广告活动竞价策略: pick one, confirm with ✓, discard with ✕. */
export function BiddingStrategyPopover({
  strategy,
  pending,
  onSubmit,
}: BiddingStrategyPopoverProps) {
  const { t } = useTranslation("campaign");
  const text = (key: string) => t(`detail.bidAdjustment.edit.${key}`);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SpBiddingStrategy | null>(strategy);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        setOpen(next);
        if (next) setDraft(strategy);
      }}
    >
      <PopoverTrigger
        render={
          <ControlButton
            variant="ghost"
            controlSize="icon-toolbar"
            aria-label={text("strategyTitle")}
            title={text("strategyTitle")}
          />
        }
      >
        <IconPencil />
      </PopoverTrigger>
      <PopoverPopup align="start" className="w-64">
        <div className="flex flex-col gap-3 text-xs">
          <p className="font-medium text-ink">{text("strategyTitle")}</p>
          <RadioGroup
            value={draft ?? ""}
            onValueChange={(value) => setDraft(value as SpBiddingStrategy)}
            aria-label={text("strategyTitle")}
            className="gap-2"
          >
            {OPTIONS.map((option) => {
              const disabled = option === "RULE_BASED";
              return (
                // biome-ignore lint/a11y/noLabelWithoutControl: Radio renders the control.
                <label
                  key={option}
                  className={cn(
                    "flex items-center gap-2 rounded-[8px] border border-line px-3 py-2.5",
                    draft === option && "border-primary bg-primary/5",
                    disabled && "opacity-60",
                  )}
                >
                  <Radio value={option} disabled={disabled} />
                  <span className="text-ink">{t(`list.biddingStrategy.${option}`)}</span>
                  {disabled ? <Badge variant="neutral">Beta</Badge> : null}
                </label>
              );
            })}
          </RadioGroup>
          <div className="flex justify-end gap-2">
            <ControlButton
              variant="default"
              controlSize="icon-toolbar"
              aria-label={text("confirm")}
              disabled={pending || !draft || draft === strategy || draft === "RULE_BASED"}
              onClick={async () => {
                if (!draft || draft === "RULE_BASED") return;
                try {
                  await onSubmit(draft);
                  setOpen(false);
                } catch {
                  // The caller reports the error; keep the choice for a retry.
                }
              }}
            >
              {pending ? <Spinner /> : <IconCheck />}
            </ControlButton>
            <ControlButton
              variant="outline"
              controlSize="icon-toolbar"
              aria-label={text("cancel")}
              disabled={pending}
              onClick={() => setOpen(false)}
            >
              <IconX />
            </ControlButton>
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}
